import { UpdateOrganizationDto } from '@geartracker/dto';
import type { Organization } from '@geartracker/types';

import { useSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales';
import axios, { endpoints } from 'src/utils/axios';
import { getErrorMessage } from 'src/utils/error-message';
import { assertDtoShape } from 'src/utils/validate-dto';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// requests
export const getOrganization = async (): Promise<Organization> => {
  const response = await axios.get(endpoints.organization);
  return response.data;
};

export const updateOrganization = async (
  organization: UpdateOrganizationDto,
): Promise<Organization> => {
  const response = await axios.post(
    endpoints.organization,
    assertDtoShape(UpdateOrganizationDto, organization, 'updateOrganization'),
  );
  return response.data;
};

// hooks
export const useOrganization = (enabled = true) => {
  return useQuery({
    queryKey: ['organization'],
    queryFn: () => getOrganization(),
    enabled,
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  return useMutation({
    mutationFn: updateOrganization,
    onSuccess: async () => {
      enqueueSnackbar(t('api.organization.update.success'));
      await queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError(error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });
};
